import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle, Clock, Download, Eye, FileText, Play, Trash2, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import { documentsApi, type Document, type DocumentStatus } from '../api'
import { useToastStore } from '../store/toast'

interface DocumentListProps {
  documents: Document[]
  onDelete?: () => void
  onRefresh?: () => void
}

const statusText: Record<DocumentStatus, string> = {
  uploaded: 'Uploaded',
  duplicate: 'Duplicate',
  queued: 'Queued',
  processing: 'Processing',
  processed: 'Processed',
  exporting: 'Exporting',
  export_ready: 'Export Ready',
  failed: 'Failed',
  deleted: 'Deleted',
}

export default function DocumentList({ documents, onDelete, onRefresh }: DocumentListProps) {
  const addToast = useToastStore((state) => state.addToast)
  const navigate = useNavigate()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedDocs = useMemo(() => documents.filter((doc) => selectedIds.includes(doc.id)), [documents, selectedIds])
  const selectedProcessableIds = selectedDocs
    .filter((doc) => ['uploaded', 'failed', 'processed', 'export_ready'].includes(doc.status))
    .map((doc) => doc.id)

  const toggleSelected = (id: string) => {
    setSelectedIds((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id])
  }

  const toggleAll = () => {
    setSelectedIds((ids) => ids.length === documents.length ? [] : documents.map((doc) => doc.id))
  }

  const startProcessing = async (doc: Document) => {
    try {
      const response = await documentsApi.startProcessing(doc.id)
      addToast({ type: 'success', message: 'Processing started.' })
      onRefresh?.()
      navigate(`/documents/${doc.id}/process`, { state: { jobId: response.data.job.id } })
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Could not start processing.' })
    }
  }

  const deleteDocument = async (doc: Document) => {
    if (!confirm(`Delete ${doc.original_filename}?`)) return
    try {
      await documentsApi.delete(doc.id)
      addToast({ type: 'success', message: 'Document deleted.' })
      onDelete?.()
    } catch {
      addToast({ type: 'error', message: 'Could not delete document.' })
    }
  }

  const startBatchProcessing = async () => {
    if (!selectedProcessableIds.length) return
    try {
      const response = await documentsApi.startProcessingBatch(selectedProcessableIds)
      addToast({
        type: response.data.errors.length ? 'warning' : 'success',
        message: response.data.errors.length
          ? `Started ${response.data.count} jobs. ${response.data.errors.length} documents were skipped.`
          : `Started ${response.data.count} processing jobs.`,
      })
      setSelectedIds([])
      onRefresh?.()
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Could not start batch processing.' })
    }
  }

  const deleteSelected = async () => {
    if (!selectedIds.length || !confirm(`Delete ${selectedIds.length} selected document${selectedIds.length === 1 ? '' : 's'}?`)) return
    try {
      const response = await documentsApi.deleteBatch(selectedIds)
      addToast({ type: 'success', message: `Deleted ${response.data.count} document${response.data.count === 1 ? '' : 's'}.` })
      setSelectedIds([])
      onDelete?.()
    } catch (err: any) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Could not delete selected documents.' })
    }
  }

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="sticky top-0 z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
          <p className="text-sm font-medium text-gray-800">
            {selectedIds.length} selected
            {selectedProcessableIds.length > 0 && <span className="text-gray-500"> • {selectedProcessableIds.length} ready to process</span>}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={startBatchProcessing}
              disabled={!selectedProcessableIds.length}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-blue px-3 py-2 text-sm text-white hover:bg-brand-accent disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              Process selected
            </button>
            <button
              onClick={deleteSelected}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete selected
            </button>
            <button onClick={() => setSelectedIds([])} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Clear
            </button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={documents.length > 0 && selectedIds.length === documents.length}
                onChange={toggleAll}
                aria-label="Select all documents"
              />
            </th>
            <Head>Document</Head>
            <Head>Status</Head>
            <Head>Uploaded</Head>
            <Head>Processed</Head>
            <Head>Rows</Head>
            <Head>Columns</Head>
            <Head>Size</Head>
            <Head align="left">Actions</Head>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {documents.map((doc) => (
            <tr key={doc.id} className="hover:bg-gray-50">
              <td className="px-4 py-4">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(doc.id)}
                  onChange={() => toggleSelected(doc.id)}
                  aria-label={`Select ${doc.original_filename}`}
                />
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-brand-blue" />
                  <div>
                    <p className="font-medium text-gray-950 max-w-xs truncate">{doc.original_filename}</p>
                    <p className="text-xs text-gray-500">{doc.sha256.slice(0, 12)}...</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4"><StatusBadge status={doc.status} /></td>
              <td className="px-6 py-4 text-gray-600">{formatDate(doc.uploaded_at)}</td>
              <td className="px-6 py-4 text-gray-600">{formatDate(doc.processed_at)}</td>
              <td className="px-6 py-4 text-gray-600">{doc.row_count || '-'}</td>
              <td className="px-6 py-4 text-gray-600">{doc.column_count || '-'}</td>
              <td className="px-6 py-4 text-gray-600">{formatBytes(doc.file_size)}</td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  {['uploaded', 'failed', 'processed', 'export_ready'].includes(doc.status) && (
                    <button onClick={() => startProcessing(doc)} title="Start processing" className="p-2 rounded-lg text-brand-blue hover:bg-brand-light">
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                  {['queued', 'processing'].includes(doc.status) && (
                    <Link to={`/documents/${doc.id}/process`} title="View processing" className="p-2 rounded-lg text-yellow-700 hover:bg-yellow-50">
                      <Clock className="w-4 h-4" />
                    </Link>
                  )}
                  {['processed', 'export_ready'].includes(doc.status) && (
                    <Link to={`/documents/${doc.id}/preview`} title="Preview/export" className="p-2 rounded-lg text-green-700 hover:bg-green-50">
                      <Eye className="w-4 h-4" />
                    </Link>
                  )}
                  {['processed', 'export_ready'].includes(doc.status) && (
                    <Link to={`/documents/${doc.id}/advanced-preview`} title="Split-screen OCR review" className="p-2 rounded-lg text-purple-700 hover:bg-purple-50">
                      <Zap className="w-4 h-4" />
                    </Link>
                  )}
                  {doc.status === 'export_ready' && (
                    <Link to={`/documents/${doc.id}/preview`} title="Download export" className="p-2 rounded-lg text-gray-700 hover:bg-gray-100">
                      <Download className="w-4 h-4" />
                    </Link>
                  )}
                  <button onClick={() => deleteDocument(doc)} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {documents.length === 0 && (
            <tr>
              <td colSpan={9} className="px-6 py-12 text-center text-gray-500">No documents yet.</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}

function Head({ children, align = 'right' }: { children: string; align?: 'left' | 'right' }) {
  return <th className={`px-6 py-3 ${align === 'left' ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{children}</th>
}

function StatusBadge({ status }: { status: DocumentStatus }) {
  const colors: Record<DocumentStatus, string> = {
    uploaded: 'bg-sky-100 text-sky-800 ring-1 ring-sky-200',
    duplicate: 'bg-orange-100 text-orange-800 ring-1 ring-orange-200',
    queued: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
    processing: 'bg-violet-100 text-violet-800 ring-1 ring-violet-200',
    processed: 'bg-green-100 text-green-800 ring-1 ring-green-200',
    exporting: 'bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200',
    export_ready: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
    failed: 'bg-red-100 text-red-800 ring-1 ring-red-200',
    deleted: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200',
  }
  const Icon = status === 'failed' ? AlertCircle : ['processed', 'export_ready'].includes(status) ? CheckCircle : Clock
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium ${colors[status]}`}>
      <Icon className="w-3.5 h-3.5" />
      {statusText[status]}
    </span>
  )
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-'
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
  return `${(bytes / Math.pow(1024, index)).toFixed(1)} ${sizes[index]}`
}
